import { useCallback, useState } from 'react';

/* 
    useFilterCount Hook
    The useFilterCount hook is designed to help developers manage and calculate the number of "filters" or form inputs that have changed or have non-default values. It is particularly useful in scenarios where filtering or searching functionality is based on user input.

    Features
    Dynamic Filter Count: The hook calculates how many form fields have been modified from their initial state or have non-empty values.
    Exclude Specific Fields: Developers can specify certain fields to ignore in the filter count by passing a comma-separated string of field keys.

    Benefits
    Reusability: Can be used across multiple forms or filter components.
    Customizable: Supports dynamic exclusion of fields using the ignoreKeys parameter.
    Performance: Optimized using useCallback to avoid unnecessary re-renders or infinite loops when used with useEffect.

    Parameters:
    paramList (Array): The list of form fields with their key and value.
    initialState (Object): The initial state of the form fields for comparison. Defaults to null.
    ignoreKeys (String): A comma-separated string of field keys to exclude from the filter count. Defaults to an empty string.

    This documentation ensures that any developer can quickly understand and implement the useFilterCount hook in their projects.
*/

const useFilterCount = () => {
    const [filterCount, setFilterCount] = useState("");

    const calculateCount = useCallback((paramList, initialModel = null, ignoreKeys = '') => {
        let count;
        const ignoreKeysArray = ignoreKeys.split(',').map((key) => key.trim());//Extract Keys to Ignore: Convert the ignoreKeys string into an array for easy lookup.
        const filteredParamList = paramList.filter((item) => !ignoreKeysArray.includes(item.key));//Filter Ignored Keys: Remove items from paramList whose key is in the ignoreKeysArray.
        const changedItems = filteredParamList.filter((item) => initialModel[item.key] !== item.value && `${item.value}`.trim() !== '');//Compare Values: For the remaining items, check if the value in initialModel is different from the current value and if the value is not empty.
        const keys = changedItems.map((item) => item.key);//Extract Keys: Collect the key of each item that passed the filters.
        count = keys.length
        setFilterCount(count)

    }, []);
    return { filterCount, calculateCount };
};

export default useFilterCount;
